import { ContestHistory } from "./ContestHistory";
import { ContestWinnerSms } from "./ContestWinnerSms";
import { ContestWinner } from "./ContestWinner";
import React, { useEffect, useRef, useState } from 'react';
import { TextField, Typography } from '@material-ui/core';
import apiURL from '../../apiURL';

export const ContestParticipants = ({ contestId }: { contestId: string }) => {
    const [eventId, setEventId] = useState("");
    const refreshRef = useRef<() => void>(() => {});
    const [eventName, setEventName] = useState("");
    const [entries, setEntries] = useState<any[]>([]);
    const [history, setHistory] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [activationError, setActivationError] = useState('');
    const [entriesError, setEntriesError] = useState('');
    const [historyError, setHistoryError] = useState('');

    useEffect(() => {
        if (!contestId) return;
        let stopped = false;
        let running = false;

        const refreshActivation = async () => {
            if (running || stopped) return;
            running = true;
            try {
                const token = localStorage.getItem('auth') || '';
                const response = await fetch(
                    `${apiURL}/contest-admin/${contestId}/activation`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error();
                const data = await response.json();
                if (stopped) return;
                const id = String(data.active_contest_id || '') === String(contestId)
                    ? String(data.active_event_id || '')
                    : '';
                setEventId(id);
                setEventName(id ? String(data.active_event_name || 'Événement sans nom') : '');
                setActivationError('');
            } catch {
                if (!stopped) setActivationError('Impossible de charger l’activation du concours.');
            } finally {
                running = false;
            }
        };

        void refreshActivation();
        const timer = window.setInterval(refreshActivation, 5000);
        return () => {
            stopped = true;
            window.clearInterval(timer);
        };
    }, [contestId]);

    useEffect(() => {
        if (!eventId) {
            setEntries([]);
            return;
        }
        let stopped = false;
        let running = false;
        let pending = false;

        const refreshEntries = async () => {
            if (stopped) return;
            if (running) {
                pending = true;
                return;
            }
            running = true;
            try {
                const token = localStorage.getItem('auth') || '';
                const response = await fetch(
                    `${apiURL}/contest-admin/${contestId}/entries/${eventId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error();
                const data = await response.json();
                if (!stopped) {
                    setEntries(Array.isArray(data) ? data : []);
                    setEntriesError('');
                }
            } catch {
                if (!stopped) setEntriesError('Impossible de charger les participants.');
            } finally {
                running = false;
                if (pending && !stopped) {
                    pending = false;
                    void refreshEntries();
                }
            }
        };

        refreshRef.current = () => { void refreshEntries(); };
        void refreshEntries();
        const timer = window.setInterval(refreshEntries, 5000);
        return () => {
            stopped = true;
            refreshRef.current = () => {};
            window.clearInterval(timer);
        };
    }, [contestId, eventId]);

    useEffect(() => {
        if (!contestId) return;
        let stopped = false;
        let running = false;

        const refreshHistory = async () => {
            if (stopped || running) return;
            running = true;
            try {
                const token = localStorage.getItem('auth') || '';
                const response = await fetch(
                    `${apiURL}/contest-admin/${contestId}/events-history`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!response.ok) throw new Error();
                const data = await response.json();
                if (!stopped) {
                    setHistory(Array.isArray(data) ? data : []);
                    setHistoryError('');
                }
            } catch {
                if (!stopped) setHistoryError('Impossible de charger l’historique.');
            } finally {
                running = false;
            }
        };

        void refreshHistory();
        const timer = window.setInterval(refreshHistory, 15000);
        return () => {
            stopped = true;
            window.clearInterval(timer);
        };
    }, [contestId]);

    const filtered = entries.filter(e =>
        !search || String(e.number).includes(search.trim())
    );

    return (
        <div style={{ padding: 20 }}>
            <Typography variant="h6">Participants du jeu concours</Typography>
            <Typography style={{ marginTop: 16, fontWeight: 600 }}>
                Événement associé : {eventName || "Aucun événement actif associé"}
            </Typography>
            <Typography style={{ marginTop: 20 }}>
                Participants affichés : {entries.length}
            </Typography>
            <TextField label="Rechercher un numéro de ticket"
                value={search} onChange={e => setSearch(e.target.value)}
                margin="normal" fullWidth />
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {['Ticket', 'Participant', 'Téléphone', 'Inscription', 'Statut', 'Lot gagné', 'SMS'].map(h =>
                                <th key={h} style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ccc' }}>{h}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(e => (
                            <tr key={e.id}>
                                <td style={{ padding: 12 }}>#{e.number}</td>
                                <td>{e.firstname}</td>
                                <td>{e.phone}</td>
                                <td>{e.createdAt ? new Date(e.createdAt).toLocaleString('fr-FR') : '—'}</td>
                                <td>{e.is_winner ? 'Gagnant' : 'Participant'}</td>
                                <td>{e.is_winner ? (e.won_prize?.name || 'Lot non renseigné') : '—'}</td>
                                <td style={{ padding: 12 }}>
                                    <ContestWinnerSms
                                        contestId={contestId}
                                        eventId={eventId}
                                        number={e.number}
                                        smsStatus={e.sms_status || 'not_sent'}
                                        simulated={Array.isArray(e.sms_history) &&
                                            e.sms_history.some((h: any) => h.status === 'simulated')}
                                        isWinner={!!e.is_winner}
                                        onSuccess={() => refreshRef.current()}
                                    />
                                    {Array.isArray(e.sms_history) && e.sms_history.length > 0 && (
                                        <details style={{marginTop: 8}}>
                                            <summary style={{cursor: 'pointer', color: '#1769aa'}}>
                                                Historique SMS ({e.sms_history.length})
                                            </summary>
                                            {e.sms_history.map((h: any, i: number) => (
                                                <div key={i} style={{marginTop: 8, padding: 8, borderBottom: '1px solid #ddd', fontSize: 12}}>
                                                    <strong>{h.status === 'sent' ? 'Envoyé' : h.status === 'failed' ? 'Échec' : h.status === 'simulated' ? 'Simulé' : h.status}</strong>
                                                    <div>{h.date ? new Date(h.date).toLocaleString('fr-FR') : 'Date inconnue'}</div>
                                                    <div>Lot : {h.prize_name || e.won_prize?.name || 'Non renseigné'}</div>
                                                    {h.provider_id && <div>Référence Brevo : {h.provider_id}</div>}
                                                    {h.error && <div style={{color: '#b42318'}}>Erreur : {h.error}</div>}
                                                </div>
                                            ))}
                                        </details>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {activationError && <Typography color="error">{activationError}</Typography>}
            {entriesError && <Typography color="error">{entriesError}</Typography>}
            {historyError && <Typography color="error">{historyError}</Typography>}

            <ContestHistory contestId={contestId} events={history.filter(e => e.id !== eventId)} />

 {eventId && (
   <ContestWinner
     contestId={contestId}
     eventId={eventId}
     onSuccess={() => refreshRef.current()}
   />
 )}
</div>
    );
};
